import React from "react";
import { ProgressStep } from "../../types";
import Header from "../components/Header";
import styles from "./LoadingView.module.css";

interface LoadingViewProps {
  steps: ProgressStep[];
  error?: string | null;
  onRetry?: () => void;
  onClose?: () => void;
}

function formatDuration(sec?: number): string {
  if (sec === undefined) return "—";
  return `${sec.toFixed(1)}s`;
}

const LOGO_URL = "assets/brand-mark-red.png";

function HeroSpinner() {
  return (
    <div className={styles.hero} aria-hidden="true">
      <svg
        className={styles.heroRingOuter}
        width="120"
        height="120"
        viewBox="0 0 120 120"
        fill="none"
      >
        <defs>
          <linearGradient id="heroGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--color-brand-red)" />
            <stop offset="100%" stopColor="var(--color-accent)" />
          </linearGradient>
        </defs>
        <circle
          cx="60"
          cy="60"
          r="54"
          stroke="var(--color-border)"
          strokeWidth="3"
          fill="none"
          opacity="0.5"
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          stroke="url(#heroGrad)"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          strokeDasharray="120 220"
        />
      </svg>
      <svg
        className={styles.heroRingInner}
        width="92"
        height="92"
        viewBox="0 0 92 92"
        fill="none"
      >
        <circle
          cx="46"
          cy="46"
          r="42"
          stroke="var(--color-accent-soft)"
          strokeWidth="2"
          fill="none"
        />
      </svg>
      <div className={styles.heroLogoWrap}>
        <img src={LOGO_URL} alt="" className={styles.heroLogo} />
      </div>
    </div>
  );
}

export default function LoadingView({ steps, error, onRetry, onClose }: LoadingViewProps) {
  if (error) {
    return (
      <div className={styles.panel}>
        <Header onBack={onClose} />
        <div className={styles.errorBody}>
          <p className={styles.errorText}>{error}</p>
          {onRetry && (
            <button className={styles.retryBtn} onClick={onRetry}>
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  const activeStep = steps.find((s) => s.status === "active");
  const completedCount = steps.filter((s) => s.status === "done").length;
  const totalCount = steps.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const headline = activeStep ? activeStep.label : "Analysing your email…";
  const subheadline = activeStep
    ? `Step ${completedCount + 1} of ${totalCount}`
    : "Preparing analysis";

  return (
    <div className={styles.panel}>
      <Header onBack={onClose} />
      <div className={styles.body}>
        <HeroSpinner />

        <div className={styles.headlineCol}>
          <p className={styles.headline}>{headline}</p>
          <p className={styles.subheadline}>{subheadline}</p>
        </div>

        <div className={styles.progressTrack} aria-hidden="true">
          <div
            className={styles.progressFill}
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <ul className={styles.stepList}>
          {steps.map((step) => (
            <li
              key={step.key}
              className={`${styles.stepRow} ${styles[`step_${step.status}`]}`}
            >
              <span className={styles.stepIcon}>
                {step.status === "done" ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : step.status === "active" ? (
                  <span className={styles.stepActiveDot} />
                ) : (
                  <span className={styles.stepPendingDot} />
                )}
              </span>
              <span className={styles.stepLabel}>{step.label}</span>
              <span className={styles.stepTime}>
                {step.status === "active"
                  ? "…"
                  : step.status === "done"
                  ? formatDuration(step.durationSec)
                  : ""}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
